import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { ColumnConfigEntry, ColumnSorting } from '@erp/shared-types';
import useAuth from '@/api/auth/useAuth';
import { useUpsertOrgTableColumnConfig } from '@/api/rpc/hooks/core/useOrgTableColumnConfig';
import { useCan } from '@/context/authz';
import { useViewMode } from '@/context/view-mode.context';
import {
  useColumnConfig,
  type ColumnViewSource,
} from '@/hooks/useColumnConfig';
import { isAdmin } from '@/utils/user';
import {
  BUYER_BOARD_LINES_TABLE_ID,
  TRANSFER_BOARD_LINES_TABLE_ID,
  buyerBoardDefaultColumns,
  moveColumn,
  withColumnWidths,
  type BoardKind,
} from '../utils/buyerBoardColumns';
import {
  BUYER_BOARD_ORDERS_TABLE_ID,
  TRANSFER_BOARD_ORDERS_TABLE_ID,
  buyerBoardOrderDefaultColumns,
} from '../utils/buyerBoardOrderColumns';
import { useBoardKind } from './boardKindContext';
import { isNumericLineColumn } from './lineTableColumns';

const TABLE_IDS: Record<BoardKind, Record<BuyerBoardTable, string>> = {
  po: {
    lines: BUYER_BOARD_LINES_TABLE_ID,
    orders: BUYER_BOARD_ORDERS_TABLE_ID,
  },
  to: {
    lines: TRANSFER_BOARD_LINES_TABLE_ID,
    orders: TRANSFER_BOARD_ORDERS_TABLE_ID,
  },
};

/** The board's two configurable tables. */
export type BuyerBoardTable = 'lines' | 'orders';

export interface BuyerBoardColumnConfig {
  table: BuyerBoardTable;
  /** Every line column in view order, hidden ones flagged. */
  columns: ColumnConfigEntry[];
  sorting: ColumnSorting;
  viewSource: ColumnViewSource;
  isLoading: boolean;
  updateColumn: (
    id: string,
    updates: Partial<Omit<ColumnConfigEntry, 'id'>>,
  ) => void;
  /** A drag in the column dialog. */
  moveColumn: (fromIndex: number, toIndex: number) => void;
  setWidths: (sizing: Readonly<Record<string, number>>) => void;
  setSorting: (sorting: ColumnSorting) => void;
  /** Drop the buyer's own view and follow the org default again. */
  reset: () => void;
  /** Set when the viewer may save the org default (admins). */
  saveAsOrgDefault: (() => void) | undefined;
}

/**
 * A buyer or transfer board table's saved view (the board from
 * {@link useBoardKind}): columns (choice, order, alignment, width,
 * header) and, for the line table, the ordered sort. An admin saves the org
 * default; a buyer's own changes save as their view on top of it.
 */
export function useBuyerBoardColumnConfig(
  table: BuyerBoardTable = 'lines',
  /** For a caller above the board's BoardKindContext provider. */
  kindOverride?: BoardKind,
): BuyerBoardColumnConfig {
  const gt = useGT();
  const contextKind = useBoardKind();
  const kind = kindOverride ?? contextKind;
  const tableId = TABLE_IDS[kind][table];
  const config = useColumnConfig(
    tableId,
    table === 'lines'
      ? buyerBoardDefaultColumns(gt, isNumericLineColumn, kind)
      : buyerBoardOrderDefaultColumns(gt, kind),
    { savedColumns: 'addNewDefaults' },
  );
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();
  const [canUpdateOrgConfig] = useCan(
    Action.Update,
    Subject.OrganizationConfig,
  );
  const { mutate: upsertOrgDefault } = useUpsertOrgTableColumnConfig();
  const { columns, sorting, replaceColumns } = config;

  const canSaveOrgDefault =
    isAdmin(currentUser, viewMode) || canUpdateOrgConfig;

  return {
    table,
    columns,
    sorting,
    viewSource: config.viewSource,
    isLoading: config.isLoading,
    updateColumn: config.updateColumn,
    moveColumn: (fromIndex, toIndex) =>
      replaceColumns(moveColumn(columns, fromIndex, toIndex)),
    setWidths: (sizing) => replaceColumns(withColumnWidths(columns, sizing)),
    setSorting: config.setSorting,
    reset: config.clearUserView,
    saveAsOrgDefault: canSaveOrgDefault
      ? () =>
          upsertOrgDefault({
            tableId,
            columns,
            sorting,
          })
      : undefined,
  };
}
